import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api, { projectAPI, eventAPI } from '../utils/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, RefreshCw, CalendarPlus, Users, Clock, MapPin, AlertCircle, CheckCircle } from 'lucide-react';
import { formatDate, formatCurrency, getEventTypeColor } from '../utils/helpers';
import { toast } from 'sonner';

const Calendar = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEventDetails, setSelectedEventDetails] = useState(null);
  const [newEventData, setNewEventData] = useState({});
  const [allProjects, setAllProjects] = useState([]);
  const [viewMode, setViewMode] = useState('month');
  const [mobileSelectedDay, setMobileSelectedDay] = useState(null);
  const [dailySchedule, setDailySchedule] = useState(null);
  const [dailyDate, setDailyDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loadingDaily, setLoadingDaily] = useState(false);

  // Generate Google Calendar URL for adding event
  const generateCalendarUrl = (event, project) => {
    const eventDate = (event.event_date || '').replace(/-/g, '');
    
    const parseTime = (timeStr) => {
      if (!timeStr) return '090000';
      const [time, period] = timeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, '0')}${(minutes || 0).toString().padStart(2, '0')}00`;
    };
    
    const startTime = parseTime(event.start_time);
    const endTime = parseTime(event.end_time);
    
    const coupleName = project?.bride_name && project?.groom_name ? ` - ${project.bride_name} & ${project.groom_name}` : '';
    const title = encodeURIComponent(`Ami Video - ${event.event_name}${coupleName}`);
    const location = encodeURIComponent(event.venue_address || event.location || '');
    const details = encodeURIComponent(`Event Type: ${event.event_type}\n${event.event_notes || ''}`);
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${eventDate}T${startTime}/${eventDate}T${endTime}&details=${details}&location=${location}`;
  };

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectAPI.getAll();
      setAllProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await eventAPI.getAll();
      const eventsData = response.data;
      
      // Convert events to have proper dates (as local dates, not UTC)
      const formattedEvents = eventsData.map(event => {
        const [year, month, day] = event.event_date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day); // month is 0-indexed
        
        return {
          ...event,
          start: localDate,
          end: localDate,
          title: event.event_name,
          source: 'app'
        };
      });
      
      setProjects(formattedEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGoogleCalendarEvents = useCallback(async () => {
    try {
      const response = await api.get('/api/calendar/google-events');
      const gEvents = response.data.events.map(event => {
        // Parse date as local date (not UTC) to avoid timezone shift
        const [year, month, day] = event.event_date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day); // month is 0-indexed
        
        return {
          ...event,
          start: localDate,
          end: localDate,
          title: event.event_name,
          source: 'google_calendar'
        };
      });
      setGoogleEvents(gEvents);
    } catch (error) {
      // Google Calendar not connected - that's ok
      console.debug('Google Calendar not connected:', error?.message);
    }
  }, []);

  const fetchDailySchedule = useCallback(async (date) => {
    if (!isAdmin) return;
    setLoadingDaily(true);
    try {
      const response = await api.get(`/api/schedule/daily/${date}`);
      setDailySchedule(response.data);
    } catch (error) {
      console.error('Failed to fetch daily schedule:', error);
      toast.error('Failed to load daily schedule');
    } finally {
      setLoadingDaily(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchEvents();
    fetchProjects();
    if (isAdmin) {
      fetchGoogleCalendarEvents();
    }
  }, [isAdmin, fetchEvents, fetchProjects, fetchGoogleCalendarEvents]);

  useEffect(() => {
    if (viewMode === 'daily' && dailyDate) {
      fetchDailySchedule(dailyDate);
    }
  }, [viewMode, dailyDate, fetchDailySchedule]);

  const handleSyncToGoogle = async () => {
    setSyncingGoogle(true);
    try {
      const response = await api.post('/api/calendar/sync-all');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to sync to Google Calendar');
    } finally {
      setSyncingGoogle(false);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Combine app events and Google Calendar events
  const allEvents = [...projects, ...googleEvents];

  const getProjectsForDay = (day) => {
    return allEvents.filter(event => {
      // Parse event_date as local date to avoid timezone issues
      const [year, month, dayNum] = event.event_date.split('-').map(Number);
      const eventDate = new Date(year, month - 1, dayNum);
      return isSameDay(day, eventDate);
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const previousYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1));
  };

  const nextYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
  };

  const handleDateClick = (day) => {
    if (!isAdmin) return; // Only admin can add events
    
    setSelectedDate(day);
    setNewEventData({
      event_name: '',
      event_type: 'Wedding',
      event_date: format(day, 'yyyy-MM-dd'),
      start_time: '09:00 AM',
      end_time: '05:00 PM',
      location: '',
      venue_address: '',
      event_notes: '',
      project_id: ''
    });
    setShowEventModal(true);
  };

  const handleEventClick = async (event, e) => {
    e.stopPropagation();
    try {
      const eventRes = await eventAPI.getOne(event.event_id);
      let projectData = null;
      try {
        const projectRes = await projectAPI.getOne(event.project_id);
        projectData = projectRes.data;
      } catch {
        projectData = { project_name: 'Project', bride_name: '', groom_name: '' };
      }
      let assignmentsData = [];
      try {
        const assignmentsRes = await eventAPI.getAssignments(event.event_id);
        assignmentsData = assignmentsRes.data;
      } catch (err) {
        console.debug('Could not fetch assignments for event:', err?.message);
      }
      
      setSelectedEventDetails({
        event: eventRes.data,
        project: projectData,
        assignments: assignmentsData
      });
      setShowEventDetailsModal(true);
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      alert('Failed to load event details');
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    if (!newEventData.project_id) {
      alert('Please select a project');
      return;
    }

    try {
      await eventAPI.create(newEventData.project_id, newEventData);
      setShowEventModal(false);
      fetchEvents(); // Refresh calendar
      alert('Event created successfully!');
    } catch (error) {
      alert('Failed to create event');
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="calendar-page">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif mb-2">Calendar</h1>
            <p className="text-muted-foreground text-sm">View all scheduled events</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button
                onClick={handleSyncToGoogle}
                disabled={syncingGoogle}
                className="px-3 py-2 text-xs rounded-full border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors flex items-center gap-2"
                data-testid="sync-to-google"
              >
                <RefreshCw size={14} className={syncingGoogle ? 'animate-spin' : ''} />
                {syncingGoogle ? 'SYNCING...' : 'SYNC TO GOOGLE'}
              </button>
            )}
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-2 text-xs transition-colors ${
                viewMode === 'month'
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border hover:bg-accent'
              }`}
              data-testid="calendar-view-month"
            >
              MONTH
            </button>
            <button
              onClick={() => setViewMode('year')}
              className={`px-3 py-2 text-xs transition-colors ${
                viewMode === 'year'
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border hover:bg-accent'
              }`}
              data-testid="calendar-view-year"
            >
              YEAR
            </button>
            {isAdmin && (
              <button
                onClick={() => setViewMode('daily')}
                className={`px-3 py-2 text-xs transition-colors ${
                  viewMode === 'daily'
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border hover:bg-accent'
                }`}
                data-testid="calendar-view-daily"
              >
                DAILY
              </button>
            )}
          </div>
        </div>

        {viewMode === 'month' ? (
          // MONTH VIEW
          <div className="card p-3 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-2xl font-serif">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <div className="flex gap-1 sm:gap-2">
              <button
                onClick={goToToday}
                className="px-3 py-2 text-xs rounded-full border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors"
                data-testid="calendar-today"
              >
                TODAY
              </button>
              <button
                onClick={previousMonth}
                className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                data-testid="calendar-prev-month"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={nextMonth}
                className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                data-testid="calendar-next-month"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Desktop Calendar Grid */}
          <div className="hidden md:block">
            <div className="grid grid-cols-7 gap-px bg-border">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="bg-background p-2 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {day}
                </div>
              ))}
              
              {days.map((day, index) => {
                const dayProjects = getProjectsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={index}
                    onClick={() => handleDateClick(day)}
                    className={`bg-background min-h-[100px] p-2 cursor-pointer hover:bg-accent transition-colors ${
                      !isCurrentMonth ? 'opacity-30' : ''
                    } ${
                      isToday ? 'ring-1 ring-gold' : ''
                    }`}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-sm mb-1 ${
                      isToday ? 'font-bold text-gold' : 'text-foreground'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayProjects.slice(0, 3).map((event, idx) => {
                        const isGoogleEvent = event.source === 'google_calendar';
                        const displayName = isGoogleEvent 
                          ? event.event_name
                          : (event.bride_name && event.groom_name) 
                            ? `${event.bride_name} & ${event.groom_name}`
                            : event.event_name;
                        const timeStr = event.start_time ? event.start_time : '';
                        
                        return (
                          <div
                            key={event.event_id || event.google_event_id}
                            onClick={(e) => !isGoogleEvent && handleEventClick(event, e)}
                            className={`text-xs px-1.5 py-0.5 truncate cursor-pointer hover:opacity-80 transition-opacity font-medium ${
                              isGoogleEvent 
                                ? 'bg-blue-500 text-white border-l-2 border-blue-700' 
                                : 'text-white'
                            }`}
                            style={!isGoogleEvent ? { backgroundColor: getEventTypeColor(event.event_type) } : {}}
                            title={`${displayName} - ${event.event_type}${timeStr ? ` @ ${timeStr}` : ''}`}
                          >
                            {timeStr && <span className="opacity-80">{timeStr} </span>}
                            {displayName}
                          </div>
                        );
                      })}
                      {dayProjects.length > 3 && (
                        <div className="text-xs text-muted-foreground font-medium">+{dayProjects.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Calendar Grid */}
          <div className="md:hidden">
            <div className="grid grid-cols-7 gap-px bg-border">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="bg-background p-1 text-center text-xs font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
              
              {days.map((day, index) => {
                const dayProjects = getProjectsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={index}
                    onClick={() => {
                      if (dayProjects.length > 0) {
                        setMobileSelectedDay(day);
                      } else {
                        handleDateClick(day);
                      }
                    }}
                    className={`bg-background min-h-[44px] p-1 cursor-pointer hover:bg-accent transition-colors text-center ${
                      !isCurrentMonth ? 'opacity-30' : ''
                    } ${
                      isToday ? 'ring-1 ring-gold' : ''
                    } ${
                      mobileSelectedDay && isSameDay(day, mobileSelectedDay) ? 'bg-gold/10' : ''
                    }`}
                    data-testid={`calendar-day-mobile-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-sm ${
                      isToday ? 'font-bold text-gold' : 'text-foreground'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    {dayProjects.length > 0 && (
                      <div className="flex justify-center gap-0.5 mt-0.5">
                        {dayProjects.slice(0, 3).map((event, idx) => (
                          <div
                            key={event.event_id || event.google_event_id || `dot-${idx}`}
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: event.source === 'google_calendar' ? '#3b82f6' : getEventTypeColor(event.event_type) }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile: Event list for selected day */}
            {mobileSelectedDay && (
              <div className="mt-4 space-y-2" data-testid="mobile-day-events">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">{format(mobileSelectedDay, 'EEEE, MMMM d')}</h3>
                  <button onClick={() => setMobileSelectedDay(null)} className="text-muted-foreground text-xs">Close</button>
                </div>
                {getProjectsForDay(mobileSelectedDay).map((event) => {
                  const isGoogleEvent = event.source === 'google_calendar';
                  return (
                    <div
                      key={event.event_id || event.google_event_id}
                      onClick={(e) => !isGoogleEvent && handleEventClick(event, e)}
                      className="p-3 rounded-xl border border-border bg-secondary cursor-pointer hover:bg-secondary transition-colors"
                      data-testid={`mobile-event-${event.event_id || event.google_event_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{event.event_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 text-white" style={{ backgroundColor: isGoogleEvent ? '#3b82f6' : getEventTypeColor(event.event_type) }}>
                              {isGoogleEvent ? 'Google' : event.event_type}
                            </span>
                            {event.start_time && <span className="text-xs text-muted-foreground">{event.start_time} - {event.end_time}</span>}
                          </div>
                          {event.bride_name && event.groom_name && (
                            <p className="text-xs text-muted-foreground mt-1">{event.bride_name} & {event.groom_name}</p>
                          )}
                          {event.location && (
                            <p className="text-xs text-muted-foreground mt-1">{event.location}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {isAdmin && (
                  <button
                    onClick={() => handleDateClick(mobileSelectedDay)}
                    className="w-full text-center text-xs text-gold py-2 border border-dashed border-gold/30 hover:border-gold"
                  >
                    + Add Event
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        ) : viewMode === 'year' ? (
          // YEAR VIEW
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-serif">{currentDate.getFullYear()}</h2>
              <div className="flex gap-2">
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-xs rounded-full border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors"
                  data-testid="calendar-today-year"
                >
                  TODAY
                </button>
                <button
                  onClick={previousYear}
                  className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextYear}
                  className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 12 }, (_, monthIndex) => {
                const monthDate = new Date(currentDate.getFullYear(), monthIndex, 1);
                const monthStart = startOfMonth(monthDate);
                const monthEnd = endOfMonth(monthDate);
                const calendarStart = startOfWeek(monthStart);
                const calendarEnd = endOfWeek(monthEnd);
                const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

                return (
                  <div key={monthIndex} className="card p-4">
                    <h3 className="text-lg font-medium mb-3 text-center">
                      {format(monthDate, 'MMMM')}
                    </h3>
                    
                    <div className="grid grid-cols-7 gap-px bg-border mb-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} className="bg-background p-1 text-center text-xs text-muted-foreground">
                          {day}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-px bg-border">
                      {days.map((day, dayIndex) => {
                        const dayProjects = getProjectsForDay(day);
                        const isCurrentMonth = isSameMonth(day, monthDate);
                        const isToday = isSameDay(day, new Date());

                        return (
                          <div
                            key={dayIndex}
                            onClick={() => handleDateClick(day)}
                            className={`bg-background p-1 text-center text-xs cursor-pointer hover:bg-accent transition-colors ${
                              !isCurrentMonth ? 'opacity-30' : ''
                            } ${
                              isToday ? 'ring-1 ring-gold' : ''
                            } ${
                              dayProjects.length > 0 ? 'font-bold' : ''
                            }`}
                            style={{
                              backgroundColor: dayProjects.length > 0 ? getEventTypeColor(dayProjects[0].event_type) + '30' : undefined
                            }}
                          >
                            <div className={isToday ? 'text-gold' : ''}>
                              {format(day, 'd')}
                            </div>
                            {dayProjects.length > 0 && (
                              <div className="text-[8px] text-gold">●</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // DAILY VIEW
          <div data-testid="daily-schedule-view">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const d = new Date(dailyDate + 'T00:00:00');
                    d.setDate(d.getDate() - 1);
                    setDailyDate(format(d, 'yyyy-MM-dd'));
                  }}
                  className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                  data-testid="daily-prev-day"
                >
                  <ChevronLeft size={18} />
                </button>
                <input
                  type="date"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  className="px-3 py-2 border border-input bg-background text-sm"
                  data-testid="daily-date-picker"
                />
                <button
                  onClick={() => {
                    const d = new Date(dailyDate + 'T00:00:00');
                    d.setDate(d.getDate() + 1);
                    setDailyDate(format(d, 'yyyy-MM-dd'));
                  }}
                  className="p-2 rounded-full border border-border hover:bg-secondary transition-colors"
                  data-testid="daily-next-day"
                >
                  <ChevronRight size={18} />
                </button>
                <button
                  onClick={() => setDailyDate(format(new Date(), 'yyyy-MM-dd'))}
                  className="px-3 py-2 text-xs rounded-full border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors"
                  data-testid="daily-today"
                >
                  TODAY
                </button>
              </div>
              {dailySchedule && (
                <div className="text-sm text-muted-foreground">
                  {dailySchedule.total_events} event{dailySchedule.total_events !== 1 ? 's' : ''} · {dailySchedule.available_crew} free · {dailySchedule.partial_crew} partial
                </div>
              )}
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif mb-4">
              {format(new Date(dailyDate + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
            </h2>

            {loadingDaily ? (
              <div className="card p-8 text-center text-muted-foreground" data-testid="daily-loading">
                Loading daily schedule...
              </div>
            ) : !dailySchedule || dailySchedule.total_events === 0 ? (
              <div className="card p-8 text-center" data-testid="daily-no-events">
                <CalendarPlus size={48} className="mx-auto mb-3 text-muted-foreground opacity-40" />
                <p className="text-muted-foreground">No events scheduled on this day.</p>
                {isAdmin && (
                  <button
                    onClick={() => handleDateClick(new Date(dailyDate + 'T00:00:00'))}
                    className="mt-4 px-4 py-2 text-xs rounded-full border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors"
                    data-testid="daily-add-event"
                  >
                    + ADD EVENT
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Events side-by-side */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
                    Events on this day
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="daily-events-grid">
                    {dailySchedule.events.map((event) => (
                      <div
                        key={event.event_id}
                        className="card p-4 border-l-4"
                        style={{ borderLeftColor: getEventTypeColor(event.event_type) }}
                        data-testid={`daily-event-${event.event_id}`}
                      >
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h4 className="font-medium text-base truncate">{event.event_name}</h4>
                            {event.project_display && (
                              <p className="text-xs text-muted-foreground truncate">{event.project_display}</p>
                            )}
                          </div>
                          <span
                            className="text-[10px] px-2 py-0.5 text-white whitespace-nowrap"
                            style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                          >
                            {event.event_type}
                          </span>
                        </div>

                        <div className="space-y-1 text-xs text-muted-foreground mb-3">
                          {event.start_time && (
                            <div className="flex items-center gap-2">
                              <Clock size={12} />
                              <span>{event.start_time} – {event.end_time}</span>
                            </div>
                          )}
                          {(event.venue_address || event.location) && (
                            <div className="flex items-start gap-2">
                              <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                              <span className="truncate">{event.venue_address || event.location}</span>
                            </div>
                          )}
                        </div>

                        <div className="pt-3 border-t border-border">
                          <div className="flex items-center gap-1 text-xs font-medium mb-2">
                            <Users size={12} />
                            <span>Assigned Crew ({event.assignments?.length || 0})</span>
                          </div>
                          {event.assignments && event.assignments.length > 0 ? (
                            <div className="space-y-1">
                              {event.assignments.map((a, i) => (
                                <div key={a.assignment_id || `${a.crew_member_id}-${i}`} className="flex items-center justify-between text-xs">
                                  <span className="truncate">{a.crew_name || 'Unknown'}</span>
                                  <span className="text-muted-foreground text-[10px] ml-2 whitespace-nowrap">
                                    {a.crew_role}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                              <AlertCircle size={12} />
                              <span>No crew assigned</span>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => navigate(`/projects?project_id=${event.project_id}&assign_event_id=${event.event_id}`)}
                          className="mt-3 w-full text-center text-[11px] text-gold py-1.5 border border-dashed border-gold/30 hover:border-gold transition-colors"
                          data-testid={`daily-manage-event-${event.event_id}`}
                        >
                          MANAGE ASSIGNMENTS
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Crew Availability */}
                <div>
                  <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
                    Crew Availability
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="crew-availability-grid">
                    {dailySchedule.crew_availability.map((crew) => {
                      const statusColor = {
                        available: 'border-l-green-500 bg-green-50/50 dark:bg-green-950/20',
                        partial: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20',
                        busy: 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20',
                        blocked: 'border-l-gray-500 bg-secondary/50 dark:bg-gray-950/20',
                      }[crew.status] || 'border-l-gray-500';

                      const statusIcon = {
                        available: <CheckCircle size={14} className="text-green-600 dark:text-green-400" />,
                        partial: <Clock size={14} className="text-amber-600 dark:text-amber-400" />,
                        busy: <AlertCircle size={14} className="text-red-600 dark:text-red-400" />,
                        blocked: <AlertCircle size={14} className="text-muted-foreground" />,
                      }[crew.status];

                      return (
                        <div
                          key={crew.crew_id}
                          className={`p-3 border-l-4 border border-border ${statusColor}`}
                          data-testid={`crew-avail-${crew.crew_id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{crew.full_name}</p>
                              <p className="text-[11px] text-muted-foreground truncate">{crew.role}</p>
                            </div>
                            {statusIcon}
                          </div>
                          <p className="text-xs mt-1 font-medium">
                            {crew.status_text}
                          </p>
                          {crew.assignments_today && crew.assignments_today.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-0.5">
                              {crew.assignments_today.map((a, i) => (
                                <div key={a.event_id || `${crew.crew_id}-${i}`} className="text-[11px] text-muted-foreground truncate">
                                  • {a.start_time}-{a.end_time} {a.event_name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Quick Add Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-serif mb-4">Add Event on {selectedDate && format(selectedDate, 'MMMM d, yyyy')}</h2>
            <form onSubmit={handleEventSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-2 uppercase tracking-wide">Select Project</label>
                <select
                  value={newEventData.project_id}
                  onChange={(e) => setNewEventData({...newEventData, project_id: e.target.value})}
                  className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  required
                >
                  <option value="">Choose a project...</option>
                  {allProjects.map(project => (
                    <option key={project.project_id} value={project.project_id}>
                      {project.project_name} - {project.client_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Event Name</label>
                  <input
                    type="text"
                    value={newEventData.event_name}
                    onChange={(e) => setNewEventData({...newEventData, event_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Event Type</label>
                  <select
                    value={newEventData.event_type}
                    onChange={(e) => setNewEventData({...newEventData, event_type: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  >
                    {['Wedding', 'Pre-wedding', 'Mehendi', 'Haldi', 'Sangeet', 'Reception', 'Engagement'].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Start Time</label>
                  <input
                    type="text"
                    value={newEventData.start_time}
                    onChange={(e) => setNewEventData({...newEventData, start_time: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    placeholder="09:00 AM"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">End Time</label>
                  <input
                    type="text"
                    value={newEventData.end_time}
                    onChange={(e) => setNewEventData({...newEventData, end_time: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    placeholder="05:00 PM"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Location</label>
                  <input
                    type="text"
                    value={newEventData.location}
                    onChange={(e) => setNewEventData({...newEventData, location: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Venue Address</label>
                  <input
                    type="text"
                    value={newEventData.venue_address}
                    onChange={(e) => setNewEventData({...newEventData, venue_address: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2 uppercase tracking-wide">Notes</label>
                <textarea
                  value={newEventData.event_notes}
                  onChange={(e) => setNewEventData({...newEventData, event_notes: e.target.value})}
                  className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  rows="3"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button type="submit" className="bg-primary text-primary-foreground px-6 py-3">
                  CREATE EVENT
                </button>
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="border border-border px-6 py-3"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEventDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-serif mb-2">{selectedEventDetails.event.event_name}</h2>
                <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-sm">
                  {selectedEventDetails.event.event_type}
                </span>
              </div>
              <button 
                onClick={() => setShowEventDetailsModal(false)}
                className="text-2xl text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>

            <div className="mb-6 p-4 bg-muted">
              <p className="text-sm text-muted-foreground mb-1">Project</p>
              <p className="text-lg font-medium">{selectedEventDetails.project.project_name}</p>
              <p className="text-sm">{selectedEventDetails.project.client_name}</p>
              {selectedEventDetails.project.bride_name && selectedEventDetails.project.groom_name && (
                <p className="text-sm text-gold">{selectedEventDetails.project.bride_name} & {selectedEventDetails.project.groom_name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Date</p>
                <p className="font-medium">{formatDate(selectedEventDetails.event.event_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Time</p>
                <p className="font-medium">{selectedEventDetails.event.start_time} - {selectedEventDetails.event.end_time}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Location</p>
                <p className="font-medium">{selectedEventDetails.event.location}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Venue</p>
                <p className="font-medium">{selectedEventDetails.event.venue_address}</p>
                {selectedEventDetails.event.venue_address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedEventDetails.event.venue_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 px-4 py-2 bg-gold/10 text-gold hover:bg-gold/20 transition-colors text-xs inline-flex items-center gap-2"
                    data-testid="calendar-directions-btn"
                  >
                    📍 GET DIRECTIONS
                  </a>
                )}
              </div>
            </div>

            {selectedEventDetails.assignments && selectedEventDetails.assignments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Assigned Crew ({selectedEventDetails.assignments.length})</h3>
                <div className="space-y-2">
                  {selectedEventDetails.assignments.map((assignment) => (
                    <div key={assignment.assignment_id} className="flex items-center justify-between p-3 border border-border">
                      <div>
                        <p className="font-medium">{assignment.crew_member_name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{(assignment.day_type || 'full_day').replace('_', ' ')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedEventDetails.event.event_notes && (
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-2">Notes</p>
                <p className="text-sm">{selectedEventDetails.event.event_notes}</p>
              </div>
            )}

            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-2">Client Contact</p>
              <p className="text-sm">{selectedEventDetails.project.client_email}</p>
              <p className="text-sm">{selectedEventDetails.project.client_phone}</p>
            </div>

            {/* Add to Calendar button */}
            <div className="mt-6">
              <a
                href={generateCalendarUrl(selectedEventDetails.event, selectedEventDetails.project)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground px-6 py-3 hover:bg-black/90 transition-colors"
                data-testid="add-to-calendar-btn"
              >
                <CalendarPlus size={18} /> Add to My Calendar
              </a>
            </div>

            <div className="mt-4 flex gap-3">
              {isAdmin && (
                <button
                  onClick={() => {
                    const pid = selectedEventDetails?.project?.project_id;
                    setShowEventDetailsModal(false);
                    navigate(pid ? `/projects?project_id=${pid}` : '/projects');
                  }}
                  className="flex-1 bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90"
                  data-testid="view-project-btn"
                >
                  VIEW PROJECT
                </button>
              )}
              {isAdmin && selectedEventDetails?.event?.event_date && (
                <button
                  onClick={() => {
                    const d = selectedEventDetails.event.event_date;
                    setShowEventDetailsModal(false);
                    navigate(`/schedule?date=${d}`);
                  }}
                  className="flex-1 border border-black/15 text-foreground px-6 py-3 hover:bg-black hover:text-white transition-colors"
                  data-testid="open-day-board-btn"
                >
                  DAY BOARD
                </button>
              )}
              <button
                onClick={() => setShowEventDetailsModal(false)}
                className={`px-6 py-3 border border-border hover:bg-accent ${!isAdmin ? 'flex-1' : ''}`}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Calendar;